import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useTranslations } from 'use-intl';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import DocumentViewerPanel from '@/components/shared/DocumentViewerPanel';
import { useCards } from '@/hooks/useCards';
import { useCardLabel } from '@/i18n/useCardLabel';
import { todosQueryRoot } from '@/hooks/useTodos';
import { ServiceRegistry } from '@/services/ServiceRegistry';
import { TodoService } from '@/services/todoService';
import type { AnswerClassification } from '@/types/card';
import type { TodoItem } from '@/types/todo';
import { userFacingErrorMessage } from '@/utils/userFacingError';

type Step = 'type' | 'holder' | 'saved';

/**
 * FIN-1165 S1 for the consultant app: the receipt beside the question. Step 1 is personal or
 * company, step 2 is whose card it is, and the answer saves the card with the classification in
 * one go. Unlike the client portal there is no "Me": a consultant has no employee row, so step 2
 * lists the company's employees only (AC4, OQ7).
 */
export function ClassifyReceiptDialog({ todo, open, onOpenChange }: {
  todo: TodoItem;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const t = useTranslations('consultantCards');
  const tc = useTranslations('consultantCards.classify');
  const cardLabel = useCardLabel();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>('type');
  const [classification, setClassification] = useState<AnswerClassification | null>(null);
  const [holderId, setHolderId] = useState('');
  const [sorted, setSorted] = useState<{ conflict: boolean; others: string | null } | null>(null);

  const documentId = todo.linked_record_id as string;
  const label = todo.detected_card ? cardLabel({ provider: todo.detected_card.provider, last_four: todo.detected_card.last_four }) : t('thisCard');

  const file = useQuery({
    queryKey: ['todo-receipt', documentId],
    queryFn: () => ServiceRegistry.getInvoiceService().getDocumentFile(documentId),
    enabled: open,
  });

  const holders = useCards(todo.company_id, open && step === 'holder');

  const answer = useMutation({
    mutationFn: () =>
      TodoService.answerCardTodo({
        todoId: todo.id,
        companyId: todo.company_id,
        classification: classification as AnswerClassification,
        holder: { kind: 'employee', employeeId: holderId },
      }),
    onSuccess: (result) => {
      if (result.status !== 'resolved') return;
      const othersKey = result.cardsSortedCapped ? 'othersCapped' : 'others';
      setSorted({
        conflict: !!result.cardConflict,
        others: result.cardsSortedCount ? tc(othersKey, { count: result.cardsSortedCount }) : null,
      });
      setStep('saved');
    },
  });

  const refusal = answer.data?.status === 'not_resolvable' ? tc('notResolvable') : null;
  const failure = answer.isError ? userFacingErrorMessage(answer.error, tc('saveFailedFallback')) : refusal;

  const close = () => {
    // The todo only leaves the list once the dialog closes, so the confirmation is never unmounted from under the person.
    if (step === 'saved') queryClient.invalidateQueries({ queryKey: todosQueryRoot });
    onOpenChange(false);
  };

  const employees = holders.data?.employees ?? [];
  const type = classification === 'expense' ? 'personal' : 'company';

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-5xl h-[85vh] flex flex-col" data-testid="classify-receipt-dialog">
        <DialogHeader>
          <DialogTitle>{file.data?.fileName ?? todo.title}</DialogTitle>
          <DialogDescription>{t('paidWith', { card: label })}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-1 min-h-0 gap-6">
          <div className="flex-1 min-w-0 border rounded-md overflow-hidden">
            {file.isLoading && (
              <div className="flex h-full items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}
            {(file.isError || (file.isSuccess && !file.data)) && (
              <div className="p-4">
                <Alert variant="destructive">
                  <AlertTitle>{tc('receiptLoadTitle')}</AlertTitle>
                  <AlertDescription>
                    {tc('receiptLoadBody')}
                    <div className="mt-2">
                      <Button size="sm" variant="outline" onClick={() => file.refetch()}>{t('tryAgain')}</Button>
                    </div>
                  </AlertDescription>
                </Alert>
              </div>
            )}
            {file.data && <DocumentViewerPanel filePath={file.data.filePath} fileName={file.data.fileName ?? undefined} />}
          </div>

          <div className="w-72 flex-none flex flex-col gap-3 justify-end">
            {step === 'type' && (
              <>
                <p className="text-sm">{tc('question')}</p>
                <div className="flex gap-2">
                  <Button variant="secondary" onClick={() => { setClassification('expense'); setStep('holder'); }}>
                    {tc('personal')}
                  </Button>
                  <Button onClick={() => { setClassification('company'); setStep('holder'); }}>{tc('company')}</Button>
                </div>
              </>
            )}

            {step === 'holder' && (
              <>
                <p className="text-sm font-medium">{tc('whose', { card: label })}</p>
                {holders.isError && (
                  <Alert variant="destructive">
                    <AlertTitle>{tc('employeesLoadTitle')}</AlertTitle>
                    <AlertDescription>
                      {userFacingErrorMessage(holders.error, tc('employeesLoadFallback'))}
                      <div className="mt-2">
                        <Button size="sm" variant="outline" onClick={() => holders.refetch()}>{t('tryAgain')}</Button>
                      </div>
                    </AlertDescription>
                  </Alert>
                )}
                {holders.isLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                {holders.isSuccess && employees.length === 0 && (
                  <p className="text-xs text-muted-foreground">{tc('noEmployees')}</p>
                )}
                {holders.isSuccess && employees.length > 0 && (
                  <div className="space-y-2">
                    <Label>{tc('employee')}</Label>
                    <Select value={holderId} onValueChange={setHolderId} disabled={answer.isPending}>
                      <SelectTrigger aria-label={tc('employee')}>
                        <SelectValue placeholder={tc('chooseEmployee')} />
                      </SelectTrigger>
                      <SelectContent>
                        {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {failure && (
                  <Alert variant="destructive">
                    <AlertTitle>{tc('errorTitle')}</AlertTitle>
                    <AlertDescription>{failure}</AlertDescription>
                  </Alert>
                )}
                <div className="flex gap-2">
                  <Button disabled={holderId === '' || answer.isPending} onClick={() => answer.mutate()}>
                    {answer.isPending && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                    {answer.isPending ? tc('saving') : tc('save')}
                  </Button>
                  <Button variant="ghost" disabled={answer.isPending} onClick={() => { answer.reset(); setStep('type'); }}>
                    {tc('back')}
                  </Button>
                </div>
              </>
            )}

            {step === 'saved' && (
              <>
                <p className="text-sm">
                  {sorted?.conflict
                    ? tc('savedConflict')
                    : tc('savedCard', { card: label, type })}
                </p>
                {sorted?.others && <p className="text-sm">{sorted.others}</p>}
                <div>
                  <Button onClick={close}>{tc('done')}</Button>
                </div>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
